/// <amd-module name="Analytics/Common/Utilities/BatchRequestUtilities" />

import * as Resources from "Analytics/Common/Resources";
import { equals } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IPageData, IVssPageService } from "VSS/Platform/Page";
import { ODataQueryOptions } from "../ODataQueryOptions";

/**
 * Generates an OData Batch request body
 */
export function generatePostPayload(getUrl: string, batchIdentifier: string, queryOptions: ODataQueryOptions): string {
    const newLine = "\n";
    return (
        `--batch_${batchIdentifier}` +
        newLine +
        "Content-Type: application/http" +
        newLine +
        "Content-Transfer-Encoding: binary" +
        newLine +
        newLine +
        `GET ${getUrl} HTTP/1.1` +
        newLine +
        (queryOptions.getProviderSyncDates ? "GetSyncDates: true" + newLine : "") +
        (queryOptions.expectSinglePage ? "X-TFS-Prefer: VSTS.Analytics.ExpectSinglePage=true" + newLine : "") +
        (queryOptions.syncDateConditions ? `X-TFS-If-SyncDates-Newer: ${JSON.stringify(queryOptions.syncDateConditions)}` + newLine : "") +
        "Prefer: omit-values=nulls" + newLine +
        newLine +
        `--batch_${batchIdentifier}`
    );
}

interface Context {
    name: string;
    id: string;
}

/** Required for enabling New Platform OData requests to properly be addressed in Batch scenario on On-premise environments  */
export interface AnalyticsHostContext {
    isHosted?: boolean;
    collection: Context;
    project: Context;
}

/** This path makes use of TFS page context. If this context is unavailable, client will throw. */
export function getHostContext(pageContext?: IVssPageContext): AnalyticsHostContext | null {
    // If page context is not available, warn consumer.
    if (!pageContext) {
        throw new Error(Resources.ODataPageContextMissing);
    }

    const vssPageService: IVssPageService = pageContext.getService<IVssPageService>("IVssPageService");
    const pageData = vssPageService.getData() as IPageData;

    // Throw if we aren't on a ProjectCollection context Host
    if (pageData.hostType !== 4) {
        throw new Error(Resources.ODataCollectionContextNotRecognized);
    }

    const vssContributionService: IVssContributionService = pageContext.getService<IVssContributionService>("IVssContributionService");
    const tfsPageData = vssContributionService.getData<any>("ms.vss-tfs-web.page-data");
    if (tfsPageData == null) {
        throw new Error(Resources.ODataProjectContextNotRecognized);
    }

    return {
        isHosted: pageData.isHosted,
        project: {
            name: tfsPageData.project.name,
            id: tfsPageData.project.id
        },
        collection: {
            name: pageData.hostName,
            id: pageData.hostId
        }
    };
}

/**
 * We "sanitize" collection from request urls path for batch requests as they cannot be evaluated correctly on the On-premise server since they do not pass through the ASP.NET request pipeline and are managed
 * by the odata batch handler directly.
 * @param getUrl
 */
export function sanitizeCollectionInPostPayload(getUrl: string, hostContext: AnalyticsHostContext | null): string {
    let modifiedRequestUrl = getUrl;

    // if host context isn't available we do not attempt to remove collection from the name
    if (hostContext && !hostContext.isHosted) {
        // Note that at the point we need to support cross collection ax queries and we will need to revisit this.
        const encodedCollectionName = encodeURI(hostContext.collection.name);
        const encodedProjectName = encodeURI(hostContext.project.name);
        const collectionId = hostContext.collection.id;

        // in the rare instance where the project and collection name are same, we make sure to not replace out the project name as that may break the request on the server.
        if (equals(encodedCollectionName, encodedProjectName, true)) {
            // if it's just collection id, remove it
            modifiedRequestUrl = getUrl.replace(new RegExp(`/${collectionId}/|/A?${collectionId}/`, "i"), "/");
            // if it's collection and project name, go to just project name
            modifiedRequestUrl = modifiedRequestUrl.replace(`/${encodedCollectionName}/${encodedProjectName}/`, `/${encodedProjectName}/`);
            // if it's just collection/project name (they are the same), no op
        } else {
            // remove either collection name OR id
            modifiedRequestUrl = getUrl.replace(new RegExp(`/${encodedCollectionName}/|/A?${collectionId}/`, "i"), "/");
        }
    }

    return modifiedRequestUrl;
}
